import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Lock, Users, Globe, AlertTriangle } from 'lucide-react';
import { useTeamStore } from '../../store/teamStore';

export type VisibilityState = 'private' | 'team' | 'public';

const SENSITIVE_KEYWORDS = [
  'password',
  'secret',
  'api key',
  'apikey',
  'token',
  'credential',
  'private key',
  'ssh',
  'aws_',
  'bearer',
];

/**
 * Scans text for sensitive keywords (case-insensitive).
 * Returns the list of matched keywords found in the text.
 */
export function detectSensitiveKeywords(text: string): string[] {
  if (!text) return [];
  const lower = text.toLowerCase();
  return SENSITIVE_KEYWORDS.filter((keyword) => lower.includes(keyword));
}

export interface VisibilitySelectorProps {
  visibility: string;
  onVisibilityChange: (visibility: string, teamIds?: number[]) => void;
  loading?: boolean;
  initialTeamIds?: number[];
  projectName?: string;
  operationDescription?: string;
}

const VisibilitySelector: React.FC<VisibilitySelectorProps> = ({
  visibility,
  onVisibilityChange,
  loading = false,
  initialTeamIds,
  projectName,
  operationDescription,
}) => {
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [showTeamSelector, setShowTeamSelector] = useState(false);
  const [selectedTeamIds, setSelectedTeamIds] = useState<number[]>(initialTeamIds ?? []);
  const teamSelectorRef = useRef<HTMLDivElement>(null);
  const { teams, loadMyTeams } = useTeamStore();

  // Detect sensitive keywords in project name and description
  const detectedKeywords = useMemo(() => {
    const combined = [projectName ?? '', operationDescription ?? ''].join(' ');
    return detectSensitiveKeywords(combined);
  }, [projectName, operationDescription]);

  // Load teams when component mounts or when team visibility is selected
  useEffect(() => {
    if (visibility === 'team' || showTeamSelector) {
      loadMyTeams();
    }
  }, [visibility, showTeamSelector, loadMyTeams]);

  // Close team selector on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        teamSelectorRef.current &&
        !teamSelectorRef.current.contains(event.target as Node)
      ) {
        setShowTeamSelector(false);
      }
    };

    if (showTeamSelector) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTeamSelector]);

  const handleVisibilityClick = (newVisibility: VisibilityState) => {
    if (loading) return;
    if (newVisibility === visibility) return;

    if (newVisibility === 'public') {
      setShowConfirmDialog(true);
    } else if (newVisibility === 'team') {
      setShowTeamSelector(true);
      loadMyTeams();
    } else {
      // private - no confirmation needed
      onVisibilityChange('private');
      setShowTeamSelector(false);
    }
  };

  const handleConfirmPublic = () => {
    setShowConfirmDialog(false);
    setShowTeamSelector(false);
    onVisibilityChange('public');
  };

  const handleCancelPublic = () => {
    setShowConfirmDialog(false);
  };

  const handleTeamToggle = (teamId: number) => {
    setSelectedTeamIds((prev) =>
      prev.includes(teamId)
        ? prev.filter((id) => id !== teamId)
        : [...prev, teamId]
    );
  };

  const handleTeamConfirm = () => {
    onVisibilityChange('team', selectedTeamIds);
    setShowTeamSelector(false);
  };

  const options: { key: VisibilityState; label: string; icon: React.ReactNode }[] = [
    {
      key: 'private',
      label: 'Private',
      icon: <Lock className="w-4 h-4" aria-hidden="true" />,
    },
    {
      key: 'team',
      label: 'Team',
      icon: <Users className="w-4 h-4" aria-hidden="true" />,
    },
    {
      key: 'public',
      label: 'Public',
      icon: <Globe className="w-4 h-4" aria-hidden="true" />,
    },
  ];

  return (
    <div className="relative">
      {/* Segmented Control */}
      <div
        className={`inline-flex rounded-lg border border-gray-300 dark:border-gray-600 ${
          loading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        role="radiogroup"
        aria-label="Project visibility"
      >
        {options.map((option) => {
          const isSelected = visibility === option.key;
          return (
            <button
              key={option.key}
              role="radio"
              aria-checked={isSelected}
              aria-label={option.label}
              disabled={loading}
              onClick={() => handleVisibilityClick(option.key)}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium transition-colors
                first:rounded-l-lg last:rounded-r-lg
                focus:outline-none focus:ring-2 focus:ring-primary-300 focus:z-10
                ${
                  isSelected
                    ? option.key === 'private'
                      ? 'bg-gray-600 text-white dark:bg-gray-500'
                      : option.key === 'team'
                      ? 'bg-blue-600 text-white dark:bg-blue-500'
                      : 'bg-green-600 text-white dark:bg-green-500'
                    : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                }
                ${loading ? 'cursor-not-allowed' : 'cursor-pointer'}
              `}
            >
              {option.icon}
              {option.label}
            </button>
          );
        })}
      </div>

      {/* Team Selector Dropdown */}
      {showTeamSelector && (
        <div
          ref={teamSelectorRef}
          className="absolute top-full left-0 mt-2 w-64 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg z-40"
          role="listbox"
          aria-label="Select teams"
        >
          <div className="p-3 border-b border-gray-200 dark:border-gray-700">
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              Select teams
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Choose which teams can access this project
            </p>
          </div>
          <div className="max-h-48 overflow-y-auto p-2">
            {teams.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400 p-2">
                No teams available
              </p>
            ) : (
              teams.map((team) => (
                <label
                  key={team.team_id}
                  className="flex items-center gap-2 p-2 rounded hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedTeamIds.includes(team.team_id)}
                    onChange={() => handleTeamToggle(team.team_id)}
                    className="rounded border-gray-300 dark:border-gray-600 dark:bg-gray-700 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-gray-900 dark:text-white">
                    {team.name}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-auto">
                    {team.member_count} members
                  </span>
                </label>
              ))
            )}
          </div>
          <div className="p-2 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-2">
            <button
              onClick={() => setShowTeamSelector(false)}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded hover:bg-gray-200 dark:hover:bg-gray-600"
            >
              Cancel
            </button>
            <button
              onClick={handleTeamConfirm}
              className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              Apply
            </button>
          </div>
        </div>
      )}

      {/* Public Confirmation Dialog */}
      {showConfirmDialog && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          role="dialog"
          aria-modal="true"
          aria-labelledby="visibility-confirm-title"
        >
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-6 max-w-md mx-4">
            <h3
              id="visibility-confirm-title"
              className="text-lg font-semibold text-gray-900 dark:text-white mb-3"
            >
              Make Project Public?
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              This project will be visible to all users. They will be able to
              view your workflows, agents, and models.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Your credentials, API keys, and run history remain private and will
              not be shared.
            </p>
            {detectedKeywords.length > 0 && (
              <div
                className="mb-4 flex items-start gap-2 rounded-md border border-yellow-300 bg-yellow-50 p-3 dark:border-yellow-600 dark:bg-yellow-900/30"
                role="alert"
                data-testid="sensitive-keyword-warning"
              >
                <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-yellow-600 dark:text-yellow-400" aria-hidden="true" />
                <p className="text-sm text-yellow-800 dark:text-yellow-200">
                  Your project contains potentially sensitive terms:{' '}
                  <strong>{detectedKeywords.join(', ')}</strong>.
                  Please review before sharing.
                </p>
              </div>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={handleCancelPublic}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmPublic}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default VisibilitySelector;
